import React, { useEffect, useMemo, useState } from "react";
import ApsModels from "../../../../models";
import ApsServices from "../../../../services";
import commonService from "../../../../services/CommonService";
import nfirsStore from "../../../../stores/NFIRSStore";
import toastStore from "../../../../stores/ToastStore";
import CommonSpinner from "../../../Common/CommonSpinner";
import {
  INFIRSTabProps,
  useNfirsTabChangedOnClick,
  useSaveOnNext,
} from "../NFIRSEntry";
import {
  FgInput,
  FgSelect,
  FgUseForm,
  useFgModel,
} from "../../../Common/FormGroups";
import { NfirsFetchStatus, useNfirsGetData } from "../../NFIRSHelper";
import ConfirmDialog from "../../../Common/ConfirmDialog";
import AdvancedDropdown from "../../../Common/AdvancedDropdown";

function NFIRSBasicIncident(props: INFIRSTabProps<any>) {
  const saveAction = () => {
    //do model pre-processing here
    let data = { ...model.new } as any;
    if (!requireFDIDandIncidentNum()) {
      data = {
        ...model.new,
        nfirsFDReceivedAidEntryId: null,
        incidentNumberOfFireDeptReceivingAid: null,
        lookupStateId: null,
        otherFDId: null,
      } as any;
    }

    return ApsServices.http.nfirsBasicIncidentService.update({
      ...data,
      nfirsIncidentRecordOutputDtos: undefined, //clear, use the input dto
    });
  };
  const {
    model,
    getModel,
    setModel,
    progress,
    setProgress,
    initModels,
    hasChanges,
    saveData,
  } = useFgModel<ApsModels.INfirsBasicIncidentInputDto>({
    objectName: "Incident",
    nfirsSectionTab: "Basic/Incident",
    promise: saveAction,
    afterSave: (data: ApsModels.INfirsBasicIncidentOutputDto) => {
      const oldAidId = model.old.nfirsAidGivenReceivedEntryId;
      const newAidId = data.nfirsAidGivenReceivedEntryId;
      const oldIncId = model.old.nfirsIncidentTypeId;
      const newIncId = data.nfirsIncidentTypeId;

      if (
        //ReValidate everything if nfirsAidGivenReceivedEntryId changes
        oldAidId !== newAidId ||
        oldIncId !== newIncId
      ) {
        nfirsStore.setNfirsAidGivenReceivedEntryId(newAidId);
        nfirsStore.setNfirsBasicIncidentType(newIncId);
        if (
          oldIncId !== newIncId || //always revalidate when incident type changes
          ([3, 4].includes(newAidId) && ![3, 4].includes(oldAidId)) ||
          (![3, 4].includes(newAidId) && [3, 4].includes(oldAidId))
        ) {
          nfirsStore.doManualValidateAll();
        }
      }
      initModels({
        ...model.new,
        nfirsFDReceivedAidEntryId: data.nfirsFDReceivedAidEntryId,
        incidentNumberOfFireDeptReceivingAid:
          data.incidentNumberOfFireDeptReceivingAid,
        lookupStateId: data.lookupStateId,
        otherFDId: data.otherFDId,
        concurrencyToken: data.concurrencyToken,
        nfirsIncidentRecordInputDtos: [
          ...(data.nfirsIncidentRecordOutputDtos.length
            ? data.nfirsIncidentRecordOutputDtos
            : []),
        ].map((rec) => {
          return {
            ...rec,
            nerisIncidentGroupId: rec.nerisIncidentGroupId,
            nerisIncidentTypeId: rec.nerisIncidentTypeId,
            nerisIncidentSubTypeId: rec.nerisIncidentSubTypeId,
          };
        }),
        nfirsAidGivenReceivedInputDtos: [
          ...(data.nfirsAidGivenReceivedOutputDtos.length
            ? data.nfirsAidGivenReceivedOutputDtos
            : []),
        ].map((rec) => {
          return {
            ...rec,
            id: rec.id,
            nerisFdId: rec.nerisFdId,
          };
        }),
      } as any);
    },
  });

  const onModelChange = (newModel: ApsModels.INfirsBasicIncidentInputDto) => {
    setModel({ ...model, new: { ...newModel } });
    //setValuesFromModel(newModel); //VALID FOR THIS TAB
  };

  const submit = async (form: any) => {
    if (!hasChanges()) {
      if (!tabChanged.isSaveOnTabChanged) {
        nfirsStore.setMoveToNextTab();
      }
      nfirsStore.setMoveToNextTab();
      return;
    }

    if (getExposureDifference() > 0) {
      nfirsStore.resetMoveTabSection();
      setShowExposureChange(true);
      return;
    }

    saveData(tabChanged.isSaveOnTabChanged);
  };

  const getExposureDifference = () => {
    return (
      Number(getModel.new.exposure || 0) - Number(getModel.old.exposure || 0)
    );
  };

  const [cntLock, setCntLock] = useState(0);
  const tabChanged = useNfirsTabChangedOnClick("Basic", "Incident", () => {
    triggerSubmitForm();
  });
  useSaveOnNext("Basic", "Incident", (lock) => {
    setCntLock(lock ? cntLock + 1 : 0);
    if (lock) {
      return;
    }

    if (!hasChanges()) {
      nfirsStore.setMoveToNextTab();
      return;
    }

    triggerSubmitForm();
    if (!formState.isValid) {
      nfirsStore.resetMoveTabSection();
    }
  });

  useEffect(() => {
    if (cntLock) {
      triggerSubmitForm();
      setTimeout(() => {
        if (!formState.isValid) {
          nfirsStore.resetMoveTabSection();
        }
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cntLock]);

  const [fireModuleShow, setFireModuleShow] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!ready) {
      return;
    }
    if (getModel.new) {
      if (props.header?.isNeris) {
        const list = [...getModel.new.nfirsIncidentRecordInputDtos].map(
          (inc) => {
            const item = incidentMapping.find(
              (x) =>
                x.subTypeId === inc.nerisIncidentSubTypeId &&
                x.typeId === inc.nerisIncidentTypeId &&
                x.groupId === inc.nerisIncidentGroupId
            );
            //console.log(item);
            return {
              ...inc,
              forFire: item?.groupId === 1, // "Fire"
              forStrucFire: item?.typeId === 15, // "Structure Fire"
              forHazmat: item?.typeId === 3, // "Hazardous Materials"
            };
          }
        );

        const showFire = !!list.find((x) => x.forFire);
        nfirsStore.setCanAcccessModule(
          ApsModels.NfirsValidationModulesEnum.Fire,
          showFire
        );
        if (!showFire) {
          nfirsStore.setCanAcccessModule(
            ApsModels.NfirsValidationModulesEnum.Arson,
            false
          );
        }
        setFireModuleShow(showFire);

        nfirsStore.setCanAcccessModule(
          ApsModels.NfirsValidationModulesEnum.StructureFire,
          !!list.find((x) => x.forStrucFire)
        );

        nfirsStore.setCanAcccessModule(
          ApsModels.NfirsValidationModulesEnum.Hazmat,
          !!list.find((x) => x.forHazmat)
        );
      } else {
        const incTyp = getModel.new.nfirsIncidentTypeId;
        const forFire = [
          100,
          111,
          112,
          ...Array.from({ length: 24 }, (_, i) => i + 120), //120-143
          ...Array.from({ length: 14 }, (_, i) => i + 160), //160-173
          ...Array.from({ length: 6 }, (_, i) => i + 113), //113-118
        ];
        const forStrucFire = [
          ...Array.from({ length: 2 }, (_, i) => i + 111), //111-112
          ...Array.from({ length: 4 }, (_, i) => i + 120), //120-123
        ];
        const forWildland = [
          160,
          561,
          631,
          632,
          ...Array.from({ length: 4 }, (_, i) => i + 140), //140-143
          ...Array.from({ length: 4 }, (_, i) => i + 170), //170-173
        ];

        nfirsStore.setCanAcccessModule(
          ApsModels.NfirsValidationModulesEnum.Wildland,
          nfirsStore.currentNfirsReport.isWildlandEnabled &&
            forWildland.indexOf(incTyp) > -1
        );

        if (
          nfirsStore.currentNfirsReport.isWildlandEnabled &&
          forWildland.indexOf(incTyp) > -1
        ) {
          nfirsStore.setCanAcccessModule(
            ApsModels.NfirsValidationModulesEnum.Fire,
            false
          );
          nfirsStore.setCanAcccessModule(
            ApsModels.NfirsValidationModulesEnum.Arson,
            false
          );
          setFireModuleShow(false);
        } else {
          const showFire = forFire.indexOf(incTyp) > -1;
          nfirsStore.setCanAcccessModule(
            ApsModels.NfirsValidationModulesEnum.Fire,
            showFire
          );
          if (!showFire) {
            nfirsStore.setCanAcccessModule(
              ApsModels.NfirsValidationModulesEnum.Arson,
              false
            );
          }
          setFireModuleShow(showFire);
        }

        nfirsStore.setCanAcccessModule(
          ApsModels.NfirsValidationModulesEnum.StructureFire,
          forStrucFire.indexOf(incTyp) > -1
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getModel.new.nfirsIncidentTypeId,
    getModel.new.nfirsIncidentRecordInputDtos,
    ready,
  ]);

  useEffect(() => {
    //This checks exising Fire/Ignition data to check if Arson module should be displayed.
    if (fireModuleShow) {
      ApsServices.http.nfirsFireIgnition
        .get(props.id)
        .then((data) => {
          if (
            data.nfirsCauseOfIgnitionEntryId === 1 ||
            data.nfirsCauseOfIgnitionEntryId === 5
          ) {
            nfirsStore.setCanAcccessModule(
              ApsModels.NfirsValidationModulesEnum.Arson,
              true
            );
          }
        })
        .catch((error) => {
          toastStore.showError("Failed Getting Ignition", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireModuleShow]);

  useEffect(() => {
    if (!progress.loading && model?.new?.id !== null) {
      const mdl = JSON.stringify(model);
      if (hasChanges()) {
        props.onChange(mdl, true);
      } else {
        props.onChange(mdl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const groups = useNfirsGetData<ApsModels.INfirsIncidentGroupOutputDto[]>(
    ApsServices.http.nfirsGenericLookupService.getNfirsIncidentGroups,
    "Incident Groups"
  );
  const types = useNfirsGetData<
    ApsModels.INfirsIncidentTypeFavoriteOutputDto[]
  >(
    () => ApsServices.http.nfirsFavorite.getIncidentTypes(false),
    "Incident Types"
  );

  const states = useNfirsGetData<ApsModels.ILookupStatesOutputDto[]>(
    async () => {
      return await ApsServices.http.genericLookupService.getStates(1);
    },
    "States"
  );
  const aidList = useNfirsGetData<
    ApsModels.INfirsAidGivenReceivedEntryOutputDto[]
  >(
    ApsServices.http.nfirsGenericLookupService.getNfirsAidGivenReceivedEntries,
    "Aid List"
  );
  const fdidReceivedAidList = useNfirsGetData<
    ApsModels.INfirsFdReceivedAidEntryOutputDto[]
  >(async () => {
    return await ApsServices.http.nfirsGenericLookupService.getNfirsFdReceivedAidEntries();
  }, "FDID Given or Received Aid List");

  const requireFDIDandIncidentNum = () => {
    const match = aidList.data?.find(
      (x) => x.id === getModel.new?.nfirsAidGivenReceivedEntryId
    );
    return (
      match?.isNeris ||
      getModel.new?.nfirsAidGivenReceivedEntryId === 3 ||
      getModel.new?.nfirsAidGivenReceivedEntryId === 4
    );
  };

  const isOtherFIDDGivenReceived = () => {
    return (
      requireFDIDandIncidentNum() &&
      !!(fdidReceivedAidList.data || []).find(
        (a) =>
          a.externalId === "Other" &&
          getModel.new?.nfirsFDReceivedAidEntryId === a.id
      )
    );
  };

  const nerisIncidentGroups = useNfirsGetData(
    ApsServices.http.nfirsGenericLookupService.getNerisIncidentGroups,
    "Incident Groups"
  );

  const nerisIncidentTypes = useNfirsGetData(
    ApsServices.http.nfirsGenericLookupService.getNerisIncidentTypes,
    "Incident Types"
  );

  const nerisIncidentSubTypes = useNfirsGetData(
    ApsServices.http.nfirsGenericLookupService.getNerisIncidentSubTypes,
    "Incident Sub Types"
  );

  const nerisIncidenFavorites = useNfirsGetData(
    async () => ApsServices.http.nfirsFavorite.getNerisIncidentFavorites(false),
    "Favorites"
  );

  useEffect(() => {
    setProgress({ loading: true });
    aidList.getData();
    states.getData();
    fdidReceivedAidList.getData();

    if (props.header?.isNeris) {
      nerisIncidentGroups.getData();
      nerisIncidentTypes.getData();
      nerisIncidentSubTypes.getData();
      nerisIncidenFavorites.getData();
    } else {
      groups.getData();
      types.getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.header?.isNeris]);

  const [incidentMapping, setIncidentMapping] = useState<
    {
      groupId: number;
      groupDescription: string;
      typeId: number;
      typeDescription: string;
      subTypeId: number;
      subTypeDescription: string;
      isFavorite?: boolean;
    }[]
  >([]);

  useEffect(() => {
    const list: {
      groupId: number;
      groupDescription: string;
      typeId: number;
      typeDescription: string;
      subTypeId: number;
      subTypeDescription: string;
      isFavorite?: boolean;
    }[] = [];
    if (
      nerisIncidentGroups.status === NfirsFetchStatus.Complete ||
      nerisIncidentTypes.status === NfirsFetchStatus.Complete ||
      nerisIncidentSubTypes.status === NfirsFetchStatus.Complete ||
      nerisIncidenFavorites.status === NfirsFetchStatus.Complete
    ) {
      const favs = nerisIncidenFavorites.data || [];
      nerisIncidentGroups.data?.forEach((grp) => {
        const types = nerisIncidentTypes.data?.filter(
          (typ) => typ.nerisIncidentGroupId === grp.id
        );
        if (!types?.length) {
          list.push({
            groupId: grp.id,
            groupDescription:
              favs.find((i) => i.groupId === grp.id)?.groupLabel ||
              grp.description,
            typeId: 0,
            typeDescription: "",
            subTypeId: 0,
            subTypeDescription: "",
            isFavorite: false,
          });
        }
        types?.forEach((typ) => {
          const subTypes = nerisIncidentSubTypes.data?.filter(
            (styp) => styp.nerisIncidentTypeId === typ.id
          );
          if (!subTypes?.length) {
            list.push({
              groupId: grp.id,
              groupDescription:
                favs.find((i) => i.groupId === grp.id)?.groupLabel ||
                grp.description,
              typeId: typ.id,
              typeDescription:
                favs.find((i) => i.typeId === typ.id)?.typeLabel ||
                typ.description,
              subTypeId: 0,
              subTypeDescription: "",
              isFavorite: false,
            });
          }
          subTypes?.forEach((styp) => {
            list.push({
              groupId: grp.id,
              groupDescription:
                favs.find((i) => i.groupId === grp.id)?.groupLabel ||
                grp.description,
              typeId: typ.id,
              typeDescription:
                favs.find((i) => i.typeId === typ.id)?.typeLabel ||
                typ.description,
              subTypeId: styp.id,
              subTypeDescription:
                favs.find((i) => i.subTypeId === styp.id)?.subTypeLabel ||
                typ.description ||
                styp.description,
              isFavorite: favs.find((i) => i.subTypeId === styp.id)?.isFavorite,
            });
          });
        });
      });
    }
    setIncidentMapping(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nerisIncidentGroups.status,
    nerisIncidentTypes.status,
    nerisIncidentSubTypes.status,
    nerisIncidenFavorites.status,
    nerisIncidenFavorites.data,
  ]);

  useEffect(() => {
    const getData = async () => {
      setProgress({ loading: true, errorCode: 0 });
      await ApsServices.http.nfirsBasicIncidentService
        .get(props.id)
        .then((data) => {
          const mdl = {
            ...model.new,
            ...data,
            reportId: Number(props.id),
            caseNumber: props.header?.number,
            eventNumber: props.header?.eventNumber,
            nfirsIncidentRecordInputDtos: [
              ...(data.nfirsIncidentRecordOutputDtos.length
                ? data.nfirsIncidentRecordOutputDtos
                : []),
            ].map((rec) => {
              return {
                ...rec,
                nerisIncidentGroupId: rec.nerisIncidentGroupId,
                nerisIncidentTypeId: rec.nerisIncidentTypeId,
                nerisIncidentSubTypeId: rec.nerisIncidentSubTypeId,
              };
            }),
            nfirsAidGivenReceivedInputDtos: [
              ...(data.nfirsAidGivenReceivedOutputDtos.length
                ? data.nfirsAidGivenReceivedOutputDtos
                : []),
            ].map((rec) => {
              return {
                ...rec,
                id: rec.id,
                nerisFdId: rec.nerisFdId,
              };
            }),
          };

          initModels(mdl as any);
          setValuesFromModel(mdl);
          setProgress({ loading: false });
          props.onChange(mdl);

          setReady(true);

          nfirsStore.setNfirsBasicIncidentType(data.nfirsIncidentTypeId || 0);
        })
        .catch((err) => {
          toastStore.showError("Failed Getting Incident detail", err);
          setProgress({ loading: false });
        });
    };

    if (
      !progress.errorCode &&
      states.status === NfirsFetchStatus.Complete &&
      aidList.status === NfirsFetchStatus.Complete &&
      fdidReceivedAidList.status === NfirsFetchStatus.Complete
    ) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    progress.errorCode,
    states.status,
    aidList.status,
    fdidReceivedAidList.status,
  ]);

  const {
    registry,
    handleSubmit,
    formState,
    setValuesFromModel,
    formRef,
    triggerSubmitForm,
    setValue,
  } = FgUseForm({
    caseNumber: {
      displayName: "Case Number/Department Number",
      validation: {
        required: false,
      },
    },
    eventNumber: {
      displayName: "Event Number",
      validation: {
        required: false,
      },
    },
    exposure: {
      displayName: "Exposure",
      validation: {
        required: false,
        max: 999,
        validate: {
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    nfirsIncidentGroupId: {
      displayName: "Incident Group",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    nfirsIncidentTypeId: {
      displayName: "Incident Type",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    nfirsAidGivenReceivedEntryId: {
      displayName: "Aid Given or Received",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    nfirsFDReceivedAidEntryId: {
      displayName: "FDID Given or Received Aid",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            // if (getModel.new && requireFDIDandIncidentNum()) {
            //   return true;
            // }
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    lookupStateId: {
      displayName: "State",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    incidentNumberOfFireDeptReceivingAid: {
      displayName: "Incident # of FDID Given Aid",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            // if (getModel.new && requireFDIDandIncidentNum()) {
            //   return true;
            // }
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
        },
      },
    },
    otherFDId: {
      displayName: "FDID of Other Aid Given",
      validation: {
        required: false,
        minLength: 5,
        maxLength: 5,
        validate: {
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
  });

  const [initialAidGivenReady, setInitialAidGivenReady] = useState(false);
  useEffect(() => {
    if (getModel.new && !progress.loading) {
      if (requireFDIDandIncidentNum()) {
        setValue(
          "nfirsFDReceivedAidEntryId",
          model.new.nfirsFDReceivedAidEntryId || undefined
        );
        setValue(
          "incidentNumberOfFireDeptReceivingAid",
          model.new.incidentNumberOfFireDeptReceivingAid || undefined
        );
        setValue("lookupStateId", model.new.lookupStateId || undefined);
        setValue("otherFDId", (model.new as any).otherFDId || undefined);
      } else {
        setValue("nfirsFDReceivedAidEntryId", undefined);
        setValue("incidentNumberOfFireDeptReceivingAid", undefined);
        setValue("lookupStateId", undefined);
        setValue("otherFDId", undefined);
      }

      if (initialAidGivenReady) {
        // nfirsStore.setNfirsAidGivenReceivedEntryId(
        //   getModel.new.nfirsAidGivenReceivedEntryId
        // );
      } else {
        setInitialAidGivenReady(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getModel.new.nfirsAidGivenReceivedEntryId]);

  const [showExposureChange, setShowExposureChange] = useState(false);
  const [parentCaseNumber] = useState<string | undefined>(
    nfirsStore.currentNfirsReport?.parentCaseNumber
  );

  const [neridAId, setNerisAid] = useState<{
    id: number;
    fdid: string;
    otherFdid: string;
    search?: string;
  }>({
    id: null as any,
    fdid: null as any,
    otherFdid: "",
  });

  const allowAddNerisAid = useMemo(() => {
    if (!neridAId.id) return false;
    if (commonService.isNullOrEmpty(neridAId.fdid)) return false;
    if (
      neridAId.fdid === "Other" &&
      commonService.isNullOrEmpty(neridAId.otherFdid)
    ) {
      return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neridAId]);

  const otherFDIDValidation = useMemo(() => {
    if (neridAId.fdid === "Other") {
      if (commonService.isNullOrEmpty(neridAId.otherFdid)) {
        return "Other FDID is required";
      }
      if (!commonService.validations.fdid(neridAId.otherFdid)) {
        return "Please use this format: FD########";
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neridAId]);

  return (
    <>
      <ConfirmDialog
        show={!!progress.errorCode}
        buttons="okonly"
        title={commonService.getConcurrencyTitle(progress.errorCode)}
        message={commonService.getConcurrencyMessage(progress.errorCode)}
        done={(rtn) => {
          if (progress.errorCode === 409) {
            setProgress({ errorCode: 0 });
          }
        }}
      />

      <ConfirmDialog
        show={showExposureChange && getExposureDifference() <= 5}
        buttons="yesno"
        title={"Exposure Changed"}
        message={`Warning: By changing the exposure to 
        <strong>${getModel.new.exposure}</strong>, 
        you are about to create 
        <strong>${getExposureDifference()}</strong> new report(s). 
        Are you sure you want to do this?`}
        done={(rtn) => {
          if (rtn === "yes") {
            saveData();
          } else {
            nfirsStore.fullResetMoveTabSection();
            if (nfirsStore.currentTabForSaveAll) {
              nfirsStore.updateCurrentTabForSaveAll(true);
            }
          }
          setShowExposureChange(false);
        }}
      />

      <ConfirmDialog
        show={showExposureChange && getExposureDifference() > 5}
        buttons="okonly"
        title={"Exposure Changed"}
        message={`Warning: By changing the exposure to 
        <strong>${getModel.new.exposure}</strong>, 
        you are about to create 
        <strong>${getExposureDifference()}</strong> new report(s). <br/><br/>
        <span style="color: red;">You may only increase 'Exposure' by a maximum of <strong>5</strong> at the time.</span>`}
        done={(rtn) => {
          nfirsStore.fullResetMoveTabSection();
          if (nfirsStore.currentTabForSaveAll) {
            nfirsStore.updateCurrentTabForSaveAll(true);
          }
          setShowExposureChange(false);
        }}
      />

      {progress.loading && (
        <div className="row">
          <div className="col-sm-12 mb-2">
            <CommonSpinner />
          </div>
        </div>
      )}
      <form
        className={`row ${progress.loading ? "display-none" : ""}`}
        onSubmit={handleSubmit(submit)}
        ref={formRef}
      >
        <div className="col-sm-12 col-lg-6">
          <div className="row">
            <div className="col-sm-12">
              <strong>Incident Details</strong>
            </div>
          </div>

          <div className="mt-3">
            <FgInput
              id="caseNumber"
              label="Case Number/Department Number"
              placeHolder="Case Number/Department Number"
              registeredField={registry.caseNumber}
              formState={formState}
              showRequiredLabel={true}
              onChange={(data) => {}}
              readOnly={true}
            ></FgInput>
          </div>

          <div className="mt-3">
            <FgInput
              id="exposure"
              label="Exposure"
              placeHolder="Exposure"
              registeredField={registry.exposure}
              formState={formState}
              showRequiredLabel={true}
              onChange={(data) => {
                onModelChange({
                  ...model.new,
                  exposure: data ? Number(data) : (undefined as any),
                });
              }}
              type="number"
              step="1"
              disabled={!!parentCaseNumber}
            ></FgInput>
          </div>

          <div className="mt-3">
            <FgInput
              id="eventNumber"
              label="Event Number / CAD Number"
              placeHolder="Event Number / CAD Number"
              registeredField={registry.eventNumber}
              formState={formState}
              showRequiredLabel={true}
              onChange={(data) => {}}
              readOnly={true}
            ></FgInput>
          </div>

          {!props.header?.isNeris && (
            <div
              key={`${props.header?.isNeris}`}
              className="alert"
              style={{ border: "solid 1px #ddd" }}
            >
              <div className="mt-2">
                <FgSelect
                  id="nfirsIncidentGroupId"
                  label="Incident Group"
                  selectMessage="- Select Incident Group -"
                  registeredField={registry.nfirsIncidentGroupId}
                  formState={formState}
                  //showRequiredLabel={true}
                  options={(groups.data || []).map((o) => {
                    return {
                      label: `${o.id} - ${o.description}`,
                      value: o.id,
                    };
                  })}
                  onChange={(data) =>
                    onModelChange({
                      ...model.new,
                      nfirsIncidentGroupId: Number(data) || (undefined as any),
                      nfirsIncidentTypeId: null as any,
                    })
                  }
                ></FgSelect>
              </div>

              <div className="mt-3 mb-2">
                <label>
                  <span className="required-label">*</span>Incident Type
                </label>
                <AdvancedDropdown
                  domId="nfirsIncidentTypeId"
                  value={model.new.nfirsIncidentTypeId}
                  favoritesHandler={{
                    add: ApsServices.http.nfirsFavorite.addFavoriteIncidentType,
                    remove:
                      ApsServices.http.nfirsFavorite.removeFavoriteIncidentType,
                    afterUpdate: (list: any[]) => types.setData(list),
                  }}
                  options={(types.data || [])
                    .filter(
                      (o) =>
                        o.nfirsIncidentGroupId ===
                        model.new.nfirsIncidentGroupId
                    )
                    .sort(commonService.sortByStringProperty("description"))
                    .map((o) => {
                      return {
                        label: `${o.description} (${o.id})`,
                        value: o.id,
                        originalObject: o,
                        isFavorite: o.isFavorite,
                        groupId: 0,
                        subGroupId: 0,
                      };
                    })}
                  onChange={(data) => {
                    onModelChange({
                      ...model.new,
                      nfirsIncidentTypeId: Number(data) || (null as any),
                    });

                    //nfirsStore.setNfirsBasicIncidentType(Number(data) || 0);
                  }}
                ></AdvancedDropdown>
              </div>
            </div>
          )}
          {props.header?.isNeris && (
            <React.Fragment key={`${props.header?.isNeris}`}>
              <div className="flex flex-row">
                <label className="required-label flex-1">Incident Types</label>
                <span>
                  <a
                    href="https://www.responserack.com/neris/incident-type-codes/"
                    target="_blank"
                  >
                    <i className="fa fa-link me-2"></i>NERIS Incident Types
                  </a>
                </span>
              </div>
              <div>
                <div className="actionTakenList">
                  {model.new.nfirsIncidentRecordInputDtos?.map((item, i) => (
                    <React.Fragment key={item.nerisIncidentSubTypeId}>
                      {item.toDelete ? (
                        <></> //show nothing...
                      ) : (
                        <div>
                          <div>
                            {nerisIncidenFavorites.data
                              ?.filter(
                                (a) =>
                                  item.nerisIncidentSubTypeId === a.subTypeId
                              )
                              ?.map((a) => {
                                return [
                                  a.groupLabel,
                                  a.typeLabel,
                                  a.subTypeLabel,
                                ]
                                  .filter(
                                    (x) => !commonService.isNullOrEmpty(x)
                                  )
                                  .join(" | ");
                              })}
                          </div>
                          <div>
                            <i
                              className="fa fa-times pointer"
                              onClick={() => {
                                let list =
                                  model.new.nfirsIncidentRecordInputDtos || [];

                                if (!item.recordId) {
                                  list = list.filter((x, ii) => i !== ii); //remove if not saved yet
                                }

                                setModel((prev) => {
                                  return {
                                    ...prev,
                                    new: {
                                      ...prev.new,
                                      nfirsIncidentRecordInputDtos:
                                        !item.recordId
                                          ? list
                                          : list.map((x, ii) => {
                                              if (i === ii) {
                                                return {
                                                  ...x,
                                                  toDelete: true,
                                                };
                                              }
                                              return x;
                                            }),
                                    },
                                  };
                                });
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="mt-1 mb-2 flex-1">
                  <AdvancedDropdown
                    domId="nfirsIncidentRecordInputDtos-subtype"
                    value={null}
                    searchPlaceholder="Search and Add More Incidents"
                    disabledOptionValues={
                      model.new.nfirsIncidentRecordInputDtos
                        ?.filter((x) => !x.toDelete)
                        ?.map((x) => x.nerisIncidentSubTypeId) || []
                    }
                    favoritesHandler={{
                      add: ApsServices.http.nfirsFavorite
                        .addFavoriteNerisIncident,
                      remove:
                        ApsServices.http.nfirsFavorite.removeNerisIncident,
                      afterUpdate: (list: any[]) => {
                        nerisIncidenFavorites.setDataAndComplete(list);
                      },
                    }}
                    groups={(nerisIncidentGroups.data || [])
                      .sort(commonService.sortByStringProperty("description"))
                      .map((g) => {
                        return {
                          groupId: g.id,
                          groupLabel:
                            nerisIncidenFavorites.data?.find(
                              (i) => i.groupId === g.id
                            )?.groupLabel || g.description,
                        };
                      })}
                    subGroups={(nerisIncidentTypes.data || [])
                      .sort(commonService.sortByStringProperty("description"))
                      .map((g) => {
                        return {
                          groupId: g.nerisIncidentGroupId,
                          subGroupId: g.id,
                          subGroupLabel:
                            nerisIncidenFavorites.data?.find(
                              (i) => i.typeId === g.id
                            )?.typeLabel || g.description,
                        };
                      })}
                    options={(incidentMapping || [])
                      .sort(
                        commonService.sortByStringProperty("subTypeDescription")
                      )
                      .map((o) => {
                        return {
                          label: `${
                            [
                              o.groupDescription,
                              o.typeDescription,
                              o.subTypeDescription,
                            ]
                              .filter((x) => !!x.trim())
                              .join(" | ")
                            //.replaceAll("_", " ")
                          }`,
                          value: o.subTypeId,
                          originalObject: o,
                          groupId: o.groupId,
                          subGroupId: o.typeId,
                          isFavorite: o.isFavorite,
                        };
                      })}
                    onChange={(data) => {
                      const val = Number(data);
                      const item = incidentMapping.find(
                        (x) => x.subTypeId === val
                      );
                      const list = model.new.nfirsIncidentRecordInputDtos || [];
                      const match = list.find(
                        (x) => x.nerisIncidentSubTypeId === item?.subTypeId
                      );
                      if (match) {
                        match.toDelete = false;
                      } else {
                        list.push({
                          recordId: null as any,
                          nerisIncidentGroupId: item
                            ? item.groupId
                            : (null as any),
                          nerisIncidentTypeId: item
                            ? item.typeId
                            : (null as any),
                          nerisIncidentSubTypeId: item
                            ? item.subTypeId
                            : (null as any),
                          toDelete: false,
                        });
                      }
                      onModelChange({
                        ...model.new,
                        nfirsIncidentRecordInputDtos:
                          commonService.deepCloneJsonArray(list),
                        _update: commonService.getUniqueId(),
                      } as any);
                    }}
                  ></AdvancedDropdown>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
        <div className="col-sm-12 col-lg-6">
          <div className="row">
            <div className="col-sm-12">
              <strong>Aid</strong>
            </div>
          </div>
          {!props.header?.isNeris && (
            <>
              <div className="mt-3">
                <FgSelect
                  id="nfirsAidGivenReceivedEntryId"
                  label="Aid Given or Received"
                  selectMessage="- Select Aid -"
                  registeredField={registry.nfirsAidGivenReceivedEntryId}
                  formState={formState}
                  showRequiredLabel={model.new.nfirsIncidentTypeId !== 611}
                  options={(aidList.data || [])
                    .filter((o) => !o.isNeris)
                    .map((o) => {
                      return {
                        label: `${o.externalId} ${o.description}`,
                        value: o.id,
                      };
                    })}
                  onChange={(data) =>
                    onModelChange({
                      ...model.new,
                      nfirsAidGivenReceivedEntryId:
                        Number(data) || (undefined as any),
                    })
                  }
                ></FgSelect>
              </div>

              <div className="mt-3">
                <FgSelect
                  id="nfirsFDReceivedAidEntryId"
                  label="FDID Given or Received Aid"
                  selectMessage="- Select FDID Given or Received Aid -"
                  registeredField={registry.nfirsFDReceivedAidEntryId}
                  formState={formState}
                  showRequiredLabel={false} //requireFDIDandIncidentNum()
                  options={(fdidReceivedAidList.data || []).map((o) => {
                    return {
                      label: `${o.description} ${o.externalId}`,
                      value: o.id,
                    };
                  })}
                  onChange={(data) =>
                    onModelChange({
                      ...model.new,
                      nfirsFDReceivedAidEntryId:
                        Number(data) || (undefined as any),
                    })
                  }
                  disabled={!requireFDIDandIncidentNum()}
                ></FgSelect>
              </div>

              <div
                className={`mt-3 ${
                  isOtherFIDDGivenReceived() ? "" : "display-none"
                }`}
              >
                <FgInput
                  id="otherFDId"
                  label="FDID of Other Aid Given"
                  placeHolder="FDID of Other Aid Given"
                  registeredField={registry.otherFDId}
                  formState={formState}
                  showRequiredLabel={false}
                  onChange={(data) => {
                    onModelChange({
                      ...model.new,
                      otherFDId: data || null,
                    } as any);
                  }}
                ></FgInput>
              </div>

              <div className="mt-3">
                <FgSelect
                  id="lookupStateId"
                  label="State"
                  selectMessage="- Select a State -"
                  registeredField={registry.lookupStateId}
                  formState={formState}
                  showRequiredLabel={false} //requireFDIDandIncidentNum()
                  options={(states.data || []).map((o) => {
                    return {
                      label: `${o.abbreviation} - ${o.description}`,
                      value: o.id,
                    };
                  })}
                  onChange={(data) =>
                    onModelChange({
                      ...model.new,
                      lookupStateId: Number(data) || (undefined as any),
                    })
                  }
                  disabled={!requireFDIDandIncidentNum()}
                ></FgSelect>
              </div>

              <div className="mt-3">
                <FgInput
                  id="incidentNumberOfFireDeptReceivingAid"
                  label="Incident # of FDID Given Aid"
                  placeHolder="Incident # of FDID Given Aid"
                  registeredField={
                    registry.incidentNumberOfFireDeptReceivingAid
                  }
                  formState={formState}
                  showRequiredLabel={false} //requireFDIDandIncidentNum()
                  onChange={(data) => {
                    onModelChange({
                      ...model.new,
                      incidentNumberOfFireDeptReceivingAid: data,
                    });
                  }}
                  readOnly={!requireFDIDandIncidentNum()}
                ></FgInput>
              </div>
            </>
          )}
          {props.header?.isNeris && (
            <div className="mt-3 mb-4" key={`${props.header?.isNeris}`}>
              <label className="mb-0">Aid Given or Received / FDID</label>
              {!!model.new.nfirsAidGivenReceivedInputDtos?.length && (
                <div className="mb-2"></div>
              )}
              <div className="actionTakenList">
                {model.new.nfirsAidGivenReceivedInputDtos?.map((aid, i) => (
                  <React.Fragment key={`${aid.id}-${i}`}>
                    {aid.toDelete ? (
                      <></>
                    ) : (
                      <div>
                        <div>
                          {aidList.data
                            ?.filter((a) => aid.id === a.id)
                            ?.map((a) => {
                              return a.description; //`${a.description} ${a.externalId ? `(${a.externalId})` : "" }`;
                            })}
                          <span className="mx-2">/</span>
                          {[
                            ...(fdidReceivedAidList.data
                              ?.filter((a) => aid.nerisFdId === a.nerisId)
                              ?.map((a) => {
                                return `${a.description} ${
                                  a.nerisId ? `(${a.nerisId})` : ""
                                }`;
                              }) || []),
                            `Other (${aid.nerisFdId})`,
                          ][0] || ""}
                        </div>
                        <div>
                          <i
                            className="fa fa-times pointer"
                            onClick={() => {
                              onModelChange({
                                ...model.new,
                                nfirsAidGivenReceivedInputDtos: (
                                  model.new.nfirsAidGivenReceivedInputDtos || []
                                ).map((rec, ii) => {
                                  if (i === ii) {
                                    return {
                                      ...rec,
                                      toDelete: true,
                                    };
                                  }
                                  return rec;
                                }),
                              });
                            }}
                          ></i>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="alert mt-0" style={{ border: "solid 1px #ddd" }}>
                <div className="flex flex-row flex-wrap" style={{ gap: "8px" }}>
                  <div className="flex-1">
                    <FgSelect
                      key={(model.new as any)._update || ""}
                      id="_nerisAid"
                      label="Aid Given or Received"
                      selectMessage="- Select Aid -"
                      formState={formState}
                      value={neridAId.id}
                      options={(aidList.data || [])
                        .filter((o) => o.isNeris)
                        .map((o) => {
                          return {
                            label: `${o.description}`, //${o.externalId}
                            value: o.id,
                          };
                        })}
                      onChange={(data) => {
                        setNerisAid((prev) => {
                          return {
                            ...prev,
                            id: Number(data),
                          };
                        });
                        commonService.focusInput("nerisOtherFdid", 500);
                      }}
                    ></FgSelect>
                  </div>
                  <div className="flex-1">
                    <label>FDID Given or Received Aid</label>
                    <div
                      id="nerisFdidDropdown"
                      className={`${neridAId.fdid ? "display-none" : ""}`}
                    >
                      <AdvancedDropdown
                        domId="neris-fdid"
                        disabled={!neridAId.id}
                        value={null}
                        searchPlaceholder="Search FDID"
                        options={[
                          ...(fdidReceivedAidList.data || []).filter(
                            (x) => x.externalId !== "Other"
                          ),
                          ...(fdidReceivedAidList.data || []).filter(
                            (x) => x.externalId === "Other"
                          ),
                        ].map((o) => {
                          return {
                            label:
                              o.externalId === "Other"
                                ? "Select Other"
                                : `${o.description} ${o.nerisId}`,
                            value: o.nerisId || "Other",
                            originalObject: o,
                            groupId: 0,
                            subGroupId: 0,
                          };
                        })}
                        onChange={(data) => {
                          const val = data;
                          setNerisAid((prev) => {
                            return {
                              ...prev,
                              fdid: val,
                            };
                          });
                          commonService.focusInput("nerisOtherFdid", 500);
                        }}
                      ></AdvancedDropdown>
                    </div>
                    <div
                      className={`${
                        neridAId.fdid ? "" : "display-none"
                      } flex flex-row alert alert-sm alert-secondary py-2 px-3`}
                    >
                      <div className="flex-1 pe-2">
                        {
                          [
                            ...(fdidReceivedAidList.data || [])
                              .filter((o) => o.nerisId === neridAId.fdid)
                              .map(
                                (o) =>
                                  `${o.description} ${
                                    o.nerisId || o.externalId
                                  }`
                              ),
                            "Select Other",
                          ][0]
                        }
                      </div>
                      <div>
                        <i
                          className="fa fa-times pointer"
                          onClick={(e) => {
                            setNerisAid((prev) => {
                              return {
                                ...prev,
                                fdid: null as any,
                              };
                            });
                            setTimeout(() => {
                              document
                                .getElementById("nerisFdidDropdown")
                                ?.querySelector("input")
                                ?.focus?.();
                            }, 200);
                          }}
                        ></i>
                      </div>
                    </div>

                    {/* <FgSelect
                      key={(model.new as any)._update || ""}
                      id="_nerisFDID"
                      label="FDID Given or Received Aid"
                      selectMessage="- Select FDID -"
                      formState={formState}
                      value={neridAId.fdid}
                      disabled={!neridAId.id}
                      options={[
                        ...(fdidReceivedAidList.data || []).filter(
                          (x) => x.externalId !== "Other"
                        ),
                        ...(fdidReceivedAidList.data || []).filter(
                          (x) => x.externalId === "Other"
                        ),
                      ].map((o) => {
                        return {
                          label: `${o.description} ${o.externalId}`,
                          value: o.externalId,
                        };
                      })}
                      onChange={(data) => {
                        setNerisAid((prev) => {
                          return {
                            ...prev,
                            fdid: data,
                          };
                        });
                      }}
                    ></FgSelect> */}
                  </div>
                </div>
                <div
                  className={`flex flex-row flex-wrap mb-3 ${
                    !!(neridAId.id && neridAId.fdid === "Other")
                      ? ""
                      : "display-none"
                  }`}
                  style={{ gap: "8px" }}
                >
                  <div className="flex-1">
                    <div className="flex flex-row">
                      <div className="flex-1">
                        <input
                          id="nerisOtherFdid"
                          type="text"
                          className="form-control"
                          value={neridAId.otherFdid || ""}
                          placeholder="Other FDID"
                          onChange={(e) => {
                            let val = e.target?.value;
                            if (val === null || val === undefined) {
                              val = "";
                            }
                            setNerisAid((prev) => {
                              return {
                                ...prev,
                                otherFdid: val,
                              };
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                        />
                      </div>
                      <div className="py-2 pl-2">or</div>
                    </div>
                    {otherFDIDValidation && (
                      <small className="text-danger">
                        {otherFDIDValidation}
                      </small>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="input-group">
                      <input
                        id="searchCity"
                        type="text"
                        className="form-control"
                        value={neridAId.search || ""}
                        placeholder="Enter City and Click Search"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            commonService.nerisOpenFDIDSearch(
                              neridAId.search || ""
                            );
                          }
                        }}
                        onChange={(e) => {
                          let val = e.target?.value;
                          if (val === null || val === undefined) {
                            val = "";
                          }
                          setNerisAid((prev) => {
                            return {
                              ...prev,
                              search: val,
                            };
                          });
                        }}
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text pointer"
                          onClick={(e) => {
                            commonService.nerisOpenFDIDSearch(
                              neridAId.search || ""
                            );
                          }}
                        >
                          <i className="fa fa-search"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!allowAddNerisAid || !!otherFDIDValidation}
                    onClick={(e) => {
                      let list = model.new.nfirsAidGivenReceivedInputDtos || [];
                      let fdid = neridAId.fdid;
                      if (fdid === "Other") {
                        fdid = neridAId.otherFdid;
                      }
                      const match = list.find(
                        (rec) =>
                          rec.id === neridAId.id && rec.nerisFdId === fdid
                      );
                      if (match) {
                        if (match.toDelete) {
                          list = list.filter(
                            (rec) =>
                              !(
                                rec.id === neridAId.id && rec.nerisFdId === fdid
                              )
                          );
                        } else {
                          toastStore.showToast(
                            "Selected Aid and FDID are already on the list",
                            "warning"
                          );
                          return; //exit
                        }
                      }

                      onModelChange({
                        ...model.new,
                        nfirsAidGivenReceivedInputDtos: [
                          ...list,
                          {
                            id: neridAId.id,
                            nerisFdId: fdid,
                            toDelete: false,
                          },
                        ],
                        _update: commonService.getUniqueId(),
                      } as any);
                      setNerisAid((prev) => {
                        return {
                          ...prev,
                          id: null as any,
                          fdid: null as any,
                          otherFdid: "",
                          search: "",
                        };
                      });
                    }}
                  >
                    Add Aid
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <button type="submit" hidden></button>
      </form>
    </>
  );
}

export default NFIRSBasicIncident;
